import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import {
  Loader2, ScanSearch, AlertTriangle, CheckCircle2, RefreshCcw, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Client-side neon-cyan detector. Loads the image into a canvas and counts
 * pixels matching the electric-cyan fingerprint (B>=170, G>=140, R<110, B-R>90).
 * Returns {cyanFraction, isNeon} for a given image URL. Never throws — a
 * fetch/decode failure resolves to {cyanFraction:0, isNeon:false, failed:true}
 * so the scan doesn't stall on a single missing image.
 */
async function scanImageForNeon(imgUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const done = (result) => resolve(result);

    img.onload = () => {
      try {
        const w = 96;
        const h = Math.max(1, Math.round((img.height / img.width) * 96));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);
        let hits = 0;
        const total = w * h;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          if (b >= 170 && g >= 140 && r < 110 && (b - r) > 90) hits++;
        }
        const cyanFraction = hits / total;
        done({ cyanFraction, isNeon: cyanFraction >= 0.22, failed: false });
      } catch {
        done({ cyanFraction: 0, isNeon: false, failed: true });
      }
    };
    img.onerror = () => done({ cyanFraction: 0, isNeon: false, failed: true });
    img.src = imgUrl;
  });
}

function ProductRow({ product, selected, onToggle, regenState }) {
  const state = regenState[product.slug];
  const thumb = product.images?.[0];

  return (
    <label
      className={`flex items-center gap-4 p-3 border rounded-lg transition-colors cursor-pointer ${
        selected
          ? 'border-[#00F0FF]/50 bg-[#00F0FF]/[0.06]'
          : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04]'
      }`}
      data-testid={`neon-row-${product.slug}`}
    >
      <input
        type="checkbox"
        className="w-5 h-5 accent-[#00F0FF] shrink-0"
        checked={selected}
        onChange={() => onToggle(product.slug)}
        disabled={!!state && state !== 'error'}
        data-testid={`neon-checkbox-${product.slug}`}
      />
      <img
        src={thumb}
        alt={product.name}
        className="w-14 h-14 rounded object-cover border border-white/10 shrink-0"
        loading="lazy"
      />
      <div className="min-w-0 flex-1">
        <div className="text-white font-semibold truncate">{product.name}</div>
        <div className="text-xs text-neutral-500 truncate">/{product.slug}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-xs text-amber-400 font-mono">
          {Math.round((product.cyanFraction || 0) * 100)}% cyan
        </div>
        {state === 'pending' && (
          <div className="text-xs text-neutral-400 flex items-center gap-1 justify-end mt-0.5">
            <Loader2 className="w-3 h-3 animate-spin" /> regenerating…
          </div>
        )}
        {state === 'done' && (
          <div className="text-xs text-emerald-400 flex items-center gap-1 justify-end mt-0.5">
            <CheckCircle2 className="w-3 h-3" /> done
          </div>
        )}
        {state === 'error' && (
          <div className="text-xs text-red-400 mt-0.5">failed</div>
        )}
      </div>
    </label>
  );
}

export default function AdminFixNeonProducts() {
  const [scanning, setScanning] = useState(false);
  const [flagged, setFlagged] = useState([]); // array of {slug, name, images, cyanFraction}
  const [checkedProducts, setCheckedProducts] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [scanErrors, setScanErrors] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [regenerating, setRegenerating] = useState(false);
  const [regenState, setRegenState] = useState({}); // slug -> pending|done|error
  const [lastScanAt, setLastScanAt] = useState(null);

  const toggle = (slug) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(slug)) n.delete(slug); else n.add(slug);
      return n;
    });
  };

  const runScan = useCallback(async () => {
    setScanning(true);
    setFlagged([]);
    setSelected(new Set());
    setRegenState({});
    setScanErrors(0);
    setCheckedProducts(0);
    setTotalProducts(0);
    try {
      const { data } = await axios.get(`${API_URL}/api/products?limit=200`);
      const items = Array.isArray(data) ? data : (data.items || data.products || []);
      setTotalProducts(items.length);

      const flaggedAcc = [];
      let errors = 0;
      // Sequential to keep the browser responsive & avoid slamming the network
      for (const p of items) {
        const hero = p.images?.[0] || p.image_url;
        if (!hero) {
          setCheckedProducts((n) => n + 1);
          continue;
        }
        const result = await scanImageForNeon(hero);
        if (result.failed) errors++;
        if (result.isNeon) {
          flaggedAcc.push({
            slug: p.slug,
            name: p.name,
            images: p.images,
            cyanFraction: result.cyanFraction,
          });
        }
        setCheckedProducts((n) => n + 1);
      }
      // Sort worst-offenders first
      flaggedAcc.sort((a, b) => b.cyanFraction - a.cyanFraction);
      setFlagged(flaggedAcc);
      setScanErrors(errors);
      setLastScanAt(new Date());
      // Pre-select everything so the user can just tap Regenerate
      setSelected(new Set(flaggedAcc.map((f) => f.slug)));
      if (flaggedAcc.length === 0) {
        toast.success("No neon products detected — you're clean!");
      } else {
        toast.warning(`${flaggedAcc.length} neon product${flaggedAcc.length === 1 ? '' : 's'} flagged`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed. Try again.');
    } finally {
      setScanning(false);
    }
  }, []);

  // Auto-run scan on first mount for a zero-tap experience.
  useEffect(() => { runScan(); }, [runScan]);

  const regenerate = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one product first.');
      return;
    }
    setRegenerating(true);
    const slugs = Array.from(selected);
    setRegenState(Object.fromEntries(slugs.map((s) => [s, 'pending'])));
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/products/regenerate-hero-batch`,
        { slugs, include_gallery: true, force_distinct: true },
        { headers: authHeaders(), timeout: 600000 },
      );
      const next = { ...regenState };
      (data.results || []).forEach((r) => {
        next[r.slug] = r.ok ? 'done' : 'error';
      });
      setRegenState(next);
      if (data.ok && data.regenerated === data.total) {
        toast.success(`Regenerated ${data.regenerated}/${data.total} — refreshing scan…`);
        // Wait a beat for CDN, then rescan.
        setTimeout(() => runScan(), 2000);
      } else {
        toast.warning(`Regenerated ${data.regenerated}/${data.total}. Some failed — see rows.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Batch regenerate failed');
      // Mark all pending rows as error
      const errState = {};
      slugs.forEach((s) => { errState[s] = 'error'; });
      setRegenState(errState);
    } finally {
      setRegenerating(false);
    }
  };

  const scanPct = totalProducts ? Math.round((checkedProducts / totalProducts) * 100) : 0;
  const allSelected = flagged.length > 0 && selected.size === flagged.length;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(flagged.map((f) => f.slug)));
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 md:p-8 max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
            <Sparkles className="w-7 h-7 text-[#00F0FF]" /> Fix Neon Products
          </h1>
          <p className="text-sm text-neutral-400 mt-2">
            Scans every product&apos;s hero image and flags any that are cyan-dominant (the tell-tale neon aesthetic).
            Regenerate any flagged product with a fresh non-neon look — one tap.
          </p>
        </div>

        {/* Scan status card */}
        <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02] space-y-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs text-neutral-400 uppercase tracking-wider">Last scan</div>
              <div className="text-white text-sm mt-0.5">
                {scanning
                  ? `Scanning… ${checkedProducts}/${totalProducts}`
                  : lastScanAt
                    ? lastScanAt.toLocaleTimeString()
                    : 'Never'}
              </div>
            </div>
            <Button
              size="lg"
              onClick={runScan}
              disabled={scanning || regenerating}
              className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black min-w-[160px]"
              data-testid="neon-scan-btn"
            >
              {scanning ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>
              ) : (
                <><ScanSearch className="w-4 h-4 mr-2" /> Scan again</>
              )}
            </Button>
          </div>
          {scanning && (
            <div className="w-full h-2 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full bg-[#00F0FF] transition-all duration-200"
                style={{ width: `${scanPct}%` }}
              />
            </div>
          )}
          {!scanning && lastScanAt && (
            <div className="text-xs text-neutral-500">
              Scanned {totalProducts} products · flagged {flagged.length}
              {scanErrors > 0 && ` · ${scanErrors} images unreadable (skipped)`}
            </div>
          )}
        </div>

        {/* Results */}
        {!scanning && flagged.length === 0 && lastScanAt && (
          <div className="p-8 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.05] text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <div className="text-white font-semibold text-lg">All clear.</div>
            <div className="text-sm text-neutral-400 mt-1">
              No products are showing the neon aesthetic.
            </div>
          </div>
        )}

        {flagged.length > 0 && (
          <>
            <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.05] flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-100">
                <div className="font-semibold">{flagged.length} product{flagged.length === 1 ? '' : 's'} flagged.</div>
                <div className="text-amber-200/80 text-xs mt-1">
                  Regenerating uses ~$0.10 per product from your Emergent LLM key and takes 15-30 sec each.
                  Each product gets a completely different non-neon aesthetic (paper collage, art-deco, Bauhaus, sumi-e, etc.).
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <button
                onClick={toggleAll}
                disabled={regenerating}
                className="text-xs text-neutral-400 hover:text-white underline underline-offset-2 disabled:opacity-40"
                data-testid="neon-toggle-all"
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </button>
              <div className="text-xs text-neutral-500">{selected.size} selected</div>
            </div>

            <div className="space-y-2">
              {flagged.map((p) => (
                <ProductRow
                  key={p.slug}
                  product={p}
                  selected={selected.has(p.slug)}
                  onToggle={toggle}
                  regenState={regenState}
                />
              ))}
            </div>

            <div className="sticky bottom-4 z-10">
              <Button
                size="lg"
                onClick={regenerate}
                disabled={regenerating || selected.size === 0}
                className="w-full bg-[#00F0FF] hover:bg-[#00C2CC] text-black text-base h-14 shadow-lg shadow-[#00F0FF]/20"
                data-testid="neon-regenerate-btn"
              >
                {regenerating ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Regenerating {selected.size}…</>
                ) : (
                  <><RefreshCcw className="w-5 h-5 mr-2" /> Regenerate {selected.size} product{selected.size === 1 ? '' : 's'}</>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
